import React, { RefObject, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import {
  CoworkerConfirmActions,
  CoworkerConfirmButton,
  CoworkerConfirmDialog,
  CoworkerConfirmOverlay,
  CoworkerConfirmText,
  CoworkerConfirmTitle,
} from './styles';

interface CoworkerGroupDeleteDialogProps {
  onCancel: VoidFunction;
  onConfirm: VoidFunction;
  /** Where the focus goes back to when the confirmation closes (the menu's trigger). */
  returnFocusRef: RefObject<HTMLElement | null>;
  /** Used when the trigger is no longer in the document (its group changed meanwhile): a mounted element. */
  fallbackFocusRef: RefObject<HTMLElement | null>;
}

const TITLE_ID = 'coworker-group-delete-title';
const TEXT_ID = 'coworker-group-delete-text';

/**
 * "Delete this group?" (RES-1232): the confirmation between the menu's Delete and the request.
 * Sits above the sheet, which has its own Escape / Tab handling at document level; this dialog takes
 * both first (capture phase on window) so Escape cancels just the confirmation and Tab cycles between
 * its two buttons instead of reaching the sheet behind. Focus starts on Cancel — the safe choice — and
 * goes back to the menu's trigger on close, since the menu that held it has already closed (or to a
 * mounted fallback if that trigger left the document meanwhile).
 */
export const CoworkerGroupDeleteDialog = ({
  onCancel,
  onConfirm,
  returnFocusRef,
  fallbackFocusRef,
}: CoworkerGroupDeleteDialogProps): JSX.Element => {
  const intl = useIntl();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // The trigger is set before this dialog mounts; read it now so the cleanup uses the same node.
    const returnFocusTarget = returnFocusRef.current;
    const fallbackFocusTarget = fallbackFocusRef.current;

    cancelRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCancel();

        return;
      }

      if (event.key === 'Tab') {
        event.stopPropagation();
        event.preventDefault();
        (document.activeElement === cancelRef.current ? confirmRef : cancelRef).current?.focus();
      }
    };

    window.addEventListener('keydown', onKeyDown, true);

    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      (returnFocusTarget?.isConnected ? returnFocusTarget : fallbackFocusTarget)?.focus();
    };
  }, [onCancel, returnFocusRef, fallbackFocusRef]);

  return (
    <>
      <CoworkerConfirmOverlay data-testid="coworker-group-delete-overlay" onClick={onCancel} />
      <CoworkerConfirmDialog
        role="alertdialog"
        aria-modal
        aria-labelledby={TITLE_ID}
        aria-describedby={TEXT_ID}
        data-testid="coworker-group-delete-dialog"
      >
        <CoworkerConfirmTitle id={TITLE_ID}>
          {intl.formatMessage({ id: 'deleteCoworkerGroupTitle' })}
        </CoworkerConfirmTitle>
        <CoworkerConfirmText id={TEXT_ID}>{intl.formatMessage({ id: 'deleteCoworkerGroupBody' })}</CoworkerConfirmText>
        <CoworkerConfirmActions>
          <CoworkerConfirmButton
            ref={cancelRef}
            type="button"
            data-testid="coworker-group-delete-cancel"
            onClick={onCancel}
          >
            {intl.formatMessage({ id: 'cancel' })}
          </CoworkerConfirmButton>
          <CoworkerConfirmButton
            ref={confirmRef}
            type="button"
            isDestructive
            data-testid="coworker-group-delete-confirm"
            onClick={onConfirm}
          >
            {intl.formatMessage({ id: 'delete' })}
          </CoworkerConfirmButton>
        </CoworkerConfirmActions>
      </CoworkerConfirmDialog>
    </>
  );
};
